import { IRenderingCommonContext } from "@commonRendering/types";
import { RendererBase } from "src/shared/renderer-base";
import { ICrossIconStyle, ILiteHeaderSectionData } from "src/typings/lite-header-section-data";
import { LiteHeaderScriptRenderer } from "./lite-header-section-script-renderer";
import { Base64Util } from "src/utils/base64-util";
import { CssProperties } from "src/models/css-property";
import { BackgroundData, IBackgroundStyle } from "@pageBuilder/typings";

export class LiteHeaderSectionRenderer extends RendererBase<ILiteHeaderSectionData> {

    private readonly scriptRenderer: LiteHeaderScriptRenderer;

    public constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);

        this.scriptRenderer = new LiteHeaderScriptRenderer();
    }

    protected override createNewInternal(): HTMLElement {
        const element = document.createElement("section");
        element.setAttribute("data-section", "liteHeaderSection");
        element.id = "lite-logo-header-section";

        const background = this.createBackgroundElement();
        const content = this.createContentElement();

        const logoContainer = this.createContainerElement("logo-container");
        const titleContainer = this.createContainerElement("title-container");
        const closeButtonContainer = this.createContainerElement("close-button-container");

        content.append(logoContainer, titleContainer, closeButtonContainer);

        element.append(background, content);
        return element;
    }

    protected override renderInternal(element: HTMLElement, data: ILiteHeaderSectionData): HTMLElement {
        const content = element.querySelector<HTMLElement>(".content");
        if (!content) {
            return element;
        }

        const logoBase64Data = Base64Util.serialize(data.logo);

        if (data.showLogo && logoBase64Data) {
            element.setAttribute("data-lite-header-logo", Base64Util.serialize(data.logo) ?? "");
        }

        this.renderContent(content, data);

        this.renderStyles(element, content, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);

        return element;
    }

    private renderContent(content: HTMLElement, data: ILiteHeaderSectionData): void {
        const logoContainer = content.querySelector(".logo-container") as HTMLElement;
        const titleContainer = content.querySelector(".title-container") as HTMLElement;
        const closeButtonContainer = content.querySelector(".close-button-container") as HTMLElement;

        if (data.showLogo) {
            this.renderLogoWrapper(logoContainer);
        } else {
            this.removeLogoWrapper(logoContainer);
        }

        if (data.showTitle) {
            this.renderTitle(titleContainer, data.title);
        } else {
            this.removeTitle(titleContainer);
        }

        this.removeGoHomeButton(closeButtonContainer);
        const button = this.renderGoHomeButton(data.crossIcon);
        closeButtonContainer.append(button);
    }

    private renderTitle(content: HTMLElement, text: string | null | undefined) {
        let titleWrapper = content.querySelector(".title-wrapper");
        if (!titleWrapper) {
            titleWrapper = document.createElement("div");
            titleWrapper.classList.add("title-wrapper");
            
            content.append(titleWrapper);
        }

        let title = content.querySelector(".title");
        
        if (!title) {
            title = document.createElement("span");
            title.classList.add("title");
            titleWrapper.append(title);
        }

        title.textContent = text ?? "";
    }

    private removeTitle(content: HTMLElement): void {
        const titleWrapper = content.querySelector(".title-wrapper");
        titleWrapper?.remove();
    }

    private renderLogoWrapper(content: HTMLElement): void {
        let logoWrapper = content.querySelector(".logo-wrapper");
        if (logoWrapper) {
            return;
        }

        logoWrapper = document.createElement("div");
        logoWrapper.classList.add("logo-wrapper");
        content.append(logoWrapper);
    }

    private removeLogoWrapper(content: HTMLElement): void {
        const logoWrapper = content.querySelector(".logo-wrapper");
        logoWrapper?.remove();
    }

    private renderGoHomeButton(style: ICrossIconStyle): HTMLElement {
        const link = document.createElement("a");
        link.href = "/";
        link.classList.add("close-button-link");

        const icon = document.createElement("span");
        icon.classList.add("icon");
        link.append(icon);

        icon.innerHTML = `
            <svg class="my-icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M4 4L23.7063 23.7063" stroke="${style.color}" stroke-width="2" stroke-linecap="round"/>
                <path d="M4 24L23.7063 4.29369" stroke="${style.color}" stroke-width="2" stroke-linecap="round"/>
            </svg>
        `;

        const closeButtonWrapper = document.createElement("div");
        closeButtonWrapper.classList.add("close-button");
        closeButtonWrapper.append(link);
        return closeButtonWrapper;
    }

    private removeGoHomeButton(content: HTMLElement): void {
        const logoWrapper = content.querySelector(".close-button");
        logoWrapper?.remove();
    }

    private renderStyles(element: HTMLElement, content: HTMLElement, data: ILiteHeaderSectionData): void {
        content.classList.remove("layout-logo-first", "layout-close-first");

        if (data.layoutType === "logo-first") {
            content.classList.add("layout-logo-first");
        }

        if (data.layoutType === "close-button-first") {
            content.classList.add("layout-close-first");
        }

        this.renderLogoStyle(element, data.logo.logoMobileHeight ?? 24);
        this.renderBackgroundStyle(element, data.background);
    }

    private renderLogoStyle(element: HTMLElement, logoMobileHeight: number): void {
        const cssItems = new CssProperties();
        cssItems.tryAdd("height", `${logoMobileHeight}px`);

        this.styleService.setStyle(element, `#${element.id} .logo-wrapper .logo`, cssItems.data);
    }

    private renderBackgroundStyle(element: HTMLElement, background: IBackgroundStyle): void {
        const props = new CssProperties();
        props.tryAdd("--lite-header-background-color", `${background?.color ?? BackgroundData.defaultHeaderData.style?.color}`);

        this.styleService.setStyle(element, `#${element.id} .background`, props.data);
    }

    private createContentElement(): HTMLElement {
        const content = document.createElement("div");
        content.classList.add("content");
        return content;
    }

    private createContainerElement(className: string): HTMLElement {
        const container = document.createElement("div");
        container.classList.add(className);
        return container;
    }

    private createBackgroundElement(): HTMLElement {
        const background = document.createElement("div");
        background.classList.add("background");
        return background;
    }

    protected override cleanupInternal(element: HTMLElement): void {
        const content = element.querySelector<HTMLElement>(".content");
        if (!content) {
            return;
        }
        content.innerHTML = "";
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [];
    }

}
